import React, { useEffect, useState } from 'react';
import { IonCheckbox, IonIcon, IonItem, IonLabel, IonChip, IonLabel as IonChipLabel } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { repeat } from 'ionicons/icons';
import dayjs from 'dayjs';
import { ITask, extractTimeFromDueDate, isDateOnly, TasksState } from './TasksState';
import { Menu, IAction } from '../components/Menu';
import { CalendarDayIcon, AIIcon, BellIcon } from '../theme/Icons';
import { Href } from '../components/Href';
import { useInstance } from '@meraki-internal/react-dependency-injection';

interface TaskListItemProps {
    task: ITask;
    zone: string;
    isLoading?: boolean;
    isHighlighted?: boolean;
    onTaskClick: (taskId: string) => void;
    onToggleComplete: (taskId: string, event?: any) => void;
    getTaskActions: (task: ITask) => IAction[];
    showDateDisplay?: boolean;
    itemRef?: (element: HTMLDivElement | null) => void;
}

const useStyles = createUseStyles({
    '@keyframes flashHighlight': {
        '0%': { backgroundColor: 'rgba(var(--ion-color-success-rgb), 0.3)' },
        '100%': { backgroundColor: 'transparent' }
    },

    taskItem: {
        '--background': 'transparent',
        '--padding-start': '8px',
        '--inner-padding-end': '16px',
        marginBottom: '8px',
        cursor: 'pointer',
        transition: 'opacity 0.2s ease, transform 0.2s ease'
    },

    highlighted: {
        animation: '$flashHighlight 2s ease-out forwards'
    },

    completedTask: {
        opacity: 0.6
    },

    skippedTask: {
        opacity: 0.5,
        '& .task-title': {
            textDecoration: 'line-through',
            color: 'var(--ion-color-medium)'
        },
        '& .task-details': {
            color: 'var(--ion-color-step-400)'
        }
    },

    taskContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    },

    taskTitle: {
        fontSize: '16px',
        fontWeight: '600',
        lineHeight: '1.4'
    },

    taskMetadata: {
        fontSize: '14px',
        color: 'var(--ion-color-medium)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },

    taskDetails: {
        fontSize: '14px',
        color: 'var(--ion-color-medium)',
        lineHeight: '1.4',
        marginTop: '4px'
    },

    completionStatus: {
        fontSize: '12px',
        color: 'var(--ion-color-medium)',
        fontStyle: 'italic',
        marginTop: '4px'
    },

    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 4,
        marginBottom: 4
    }
});

// Same color logic as TagsField and other list components
const getTagColor = (tag: string) => {
    const tagColorOptions = [
        '#4A8FA8', // Teal (primary theme equivalent)
        '#8E5FC7', // Purple (tertiary theme equivalent)
        '#D45A2A', // Orange (secondary theme equivalent)
        '#4A8F6B', // Green (success theme equivalent)
        '#5A72C7', // Blue
        '#C768A3', // Pink
        '#3E4A5C', // Dark blue-gray
        '#D47A47', // Orange-brown
        '#C73E3E'  // Red
    ];

    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
        const char = tag.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const colorIndex = Math.abs(hash) % tagColorOptions.length;
    const color = tagColorOptions[colorIndex];

    return {
        backgroundColor: `${color}1A`, // 10% opacity
        color: color,
        border: `1px solid ${color}`
    };
};

const TaskListItemTags: React.FC<{ tags?: string[]; isAIGenerated?: boolean; hideAIIcon?: boolean }> = ({ tags, isAIGenerated, hideAIIcon }) => {
    const classes = useStyles();

    const hasRegularTags = tags && tags.length > 0;
    const showAIChip = isAIGenerated && !hideAIIcon;

    if (!hasRegularTags && !showAIChip) {
        return null;
    }

    const maxTagsToShow = 3;
    const visibleTags = hasRegularTags ? tags.slice(0, maxTagsToShow) : [];
    const remainingCount = hasRegularTags ? Math.max(0, tags.length - maxTagsToShow) : 0;

    return (
        <div className={classes.tagsContainer}>
            {showAIChip && (
                <IonChip style={{
                    backgroundColor: 'transparent',
                    color: '#4A8FA8',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    paddingLeft: 0,
                    paddingRight: 0
                }} data-id="task-ai-chip">
                    <AIIcon color="primary" />
                </IonChip>
            )}
            {visibleTags.map(tag => (
                <IonChip key={tag} style={getTagColor(tag)}>
                    <IonChipLabel style={{ fontSize: '12px' }}>{tag}</IonChipLabel>
                </IonChip>
            ))}
            {remainingCount > 0 && (
                <IonChip style={{ backgroundColor: 'var(--ion-color-step-100)', color: 'var(--ion-color-step-600)' }}>
                    <IonChipLabel style={{ fontSize: '12px' }}>+{remainingCount} more</IonChipLabel>
                </IonChip>
            )}
        </div>
    );
};

// Linkify function to detect and make links clickable
const linkifyText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
        if (part && (part.startsWith('http://') || part.startsWith('https://') || part.startsWith('www.'))) {
            const url = part.startsWith('www.') ? `https://${part}` : part;
            return (
                <Href
                    key={index}
                    onClick={() => window.open(url, '_blank')}
                >
                    {part}
                </Href>
            );
        }
        return part;
    });
};

const formatTime = (timeString: string): string => {
    return timeString.toLowerCase(); // Convert AM/PM to am/pm
};

const formatTaskDate = (dueDate: string): dayjs.Dayjs => {
    if (isDateOnly(dueDate)) {
        // For date-only tasks, parse as local date to avoid timezone shifts
        return dayjs(dueDate + 'T00:00:00');
    } else {
        // For datetime tasks, parse normally (this will be in user's timezone)
        return dayjs(dueDate);
    }
};

const getDateDisplayForTask = (task: ITask, zone: string): string | null => {
    // Today and tomorrow tasks don't show date (it's in the header)
    if (zone === 'today' || zone === 'tomorrow') {
        return null;
    }

    // Note context always shows date
    if (zone === 'note') {
        const noteTaskDate = formatTaskDate(task.dueDate || '').startOf('day');
        const noteToday = dayjs().startOf('day');
        const noteDaysDiff = noteTaskDate.diff(noteToday, 'days');

        if (noteDaysDiff === 0) {
            return 'Today';
        } else if (noteDaysDiff === 1) {
            return 'Tomorrow';
        } else if (noteDaysDiff === -1) {
            return 'Yesterday';
        } else if (noteDaysDiff < 0) {
            // Past dates: use date format (not day name to avoid confusion)
            return noteTaskDate.format('MMM D'); // Jan 15, etc.
        } else if (noteDaysDiff > 0 && noteDaysDiff <= 7) {
            // Future dates within 7 days: show day name
            return noteTaskDate.format('dddd'); // Monday, Tuesday, etc.
        } else {
            // Beyond 7 days: show date
            return noteTaskDate.format('MMM D'); // Jan 15, etc.
        }
    }

    // For overdue tasks, show the original due date
    if (zone === 'overdue') {
        const overdueTaskDate = formatTaskDate(task.dueDate || '').startOf('day');
        const todayDate = dayjs().startOf('day');
        const daysDifference = Math.abs(overdueTaskDate.diff(todayDate, 'days'));

        if (daysDifference === 1) {
            return 'Yesterday';
        } else {
            // All other past dates: use date format (not day name to avoid confusion)
            return overdueTaskDate.format('MMM D'); // Oct 7, etc.
        }
    }

    // For upcoming tasks, use smart formatting with proper timezone handling
    const taskDate = formatTaskDate(task.dueDate || '').startOf('day');
    const today = dayjs().startOf('day');
    const daysDiff = taskDate.diff(today, 'days');

    if (daysDiff <= 7) {
        // Next 7 days: show day name
        return taskDate.format('dddd');
    } else {
        // Beyond 7 days: show date without year
        return taskDate.format('MMM D');
    }
};

export const TaskListItem: React.FC<TaskListItemProps> = ({
    task,
    zone,
    isLoading = false,
    isHighlighted = false,
    onTaskClick,
    onToggleComplete,
    getTaskActions,
    showDateDisplay = true,
    itemRef
}) => {
    const classes = useStyles();
    const tasksState = useInstance(TasksState);
    const [reminderEnabled, setReminderEnabled] = useState(false);

    // Load reminder preference
    useEffect(() => {
        const taskTime = extractTimeFromDueDate(task.dueDate || '');

        if (task.taskId && taskTime) {
            tasksState.getReminderPreference(task.taskId)
                .then(result => setReminderEnabled(result.enabled))
                .catch(() => setReminderEnabled(false));
        } else {
            setReminderEnabled(false);
        }
    }, [task.taskId, task.dueDate, tasksState, tasksState.state.reminderPreferences]);

    return (
        <div ref={itemRef}>
        <IonItem
            key={task.taskId}
            className={`${classes.taskItem} ${task.completed ? classes.completedTask : ''} ${task.skippedAt ? classes.skippedTask : ''} ${isHighlighted ? classes.highlighted : ''}`}
            style={{
                alignItems: 'flex-start',
                opacity: isLoading ? 0.5 : 1,
                pointerEvents: isLoading ? 'none' : 'auto',
                transition: 'opacity 0.2s, transform 0.2s'
            }}
            button
            onClick={() => onTaskClick(task.taskId)}
            data-id="task-item"
            data-description={task.description}
            data-task-id={task.taskId}
        >
            <IonCheckbox
                slot="start"
                checked={task.completed || false}
                onIonChange={(e) => onToggleComplete(task.taskId, e)}
                onClick={(e) => e.stopPropagation()}
                data-id="task-checkbox"
                aria-label={`Mark task "${task.description}" as ${task.completed ? 'incomplete' : 'complete'}`}
                style={{
                    marginTop: 11,
                    marginRight: 12,
                    '--size': '20px',
                    '--checkbox-background-checked': 'var(--ion-color-success)',
                    '--border-color-checked': 'var(--ion-color-success)',
                    alignSelf: 'flex-start'
                }}
            />
            <IonLabel>
                <div className={classes.taskContent}>
                    <div className={`${classes.taskTitle} task-title`} style={{
                        textDecoration: task.completed || task.skippedAt ? 'line-through' : 'none',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '8px'
                    }}>
                        <span style={{ flex: 1 }}>{task.description}</span>
                        <div style={{marginTop: -10, marginBottom: -10}}>
                            <Menu
                                data-id={`task-menu-${task.taskId}`}
                                actions={getTaskActions(task)}
                            />
                        </div>
                    </div>

                    {(() => {
                        const dateDisplay = showDateDisplay ? getDateDisplayForTask(task, zone) : null;
                        const taskTime = extractTimeFromDueDate(task.dueDate || '');
                        const hasDateOrTime = dateDisplay || taskTime;

                        // Determine color based on zone and timing
                        let color = 'medium';
                        let iconColor: 'medium' | 'secondary' | 'tertiary' = 'medium';

                        // Check if task is overdue (for both 'overdue' zone and 'note' zone)
                        const taskDate = formatTaskDate(task.dueDate || '').startOf('day');
                        const today = dayjs().startOf('day');
                        const daysDiff = taskDate.diff(today, 'days');
                        const isOverdue = daysDiff < 0;

                        if (zone === 'overdue' || (zone === 'note' && isOverdue)) {
                            // Overdue tasks use secondary color (orange)
                            color = 'var(--ion-color-secondary)';
                            iconColor = 'secondary';
                        } else {
                            // Check if task is within 7 days for tertiary color
                            const isWithin7Days = zone === 'today' || zone === 'tomorrow' || (daysDiff > 0 && daysDiff <= 7);

                            if (isWithin7Days) {
                                color = 'var(--ion-color-tertiary)';
                                iconColor = 'tertiary';
                            }
                        }

                        // Show metadata if there's date/time info, recurring info, or reminder
                        if (hasDateOrTime || task.recurrenceRule || reminderEnabled) {
                            return (
                                <div className={classes.taskMetadata} style={{ color }}>
                                    {dateDisplay && (
                                        <>
                                            <CalendarDayIcon size="small" color={iconColor} />
                                            <span>{dateDisplay}</span>
                                        </>
                                    )}
                                    {taskTime && (
                                        <>
                                            {dateDisplay && <span style={{ marginLeft: '4px' }} />}
                                            <span data-id="task-when">{formatTime(taskTime)}</span>
                                        </>
                                    )}
                                    {task.recurrenceRule && (
                                        <>
                                            <span style={{ marginLeft: '8px' }} />
                                            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                                <IonIcon
                                                    icon={repeat}
                                                    color={iconColor}
                                                    style={{ fontSize: '14px' }}
                                                    data-id="task-recurring-icon"
                                                />
                                                {task.recurrenceEnded && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-2px',
                                                            right: '-2px',
                                                            width: '8px',
                                                            height: '8px',
                                                            backgroundColor: 'var(--ion-color-success)',
                                                            borderRadius: '50%',
                                                            border: '1px solid white'
                                                        }}
                                                        data-id="task-completed-forever-indicator"
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {reminderEnabled && (
                                        <>
                                            <span style={{ marginLeft: '8px' }} />
                                            <BellIcon size="small" color="secondary" data-id="task-reminder-icon" />
                                        </>
                                    )}
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })()}

                    {task.details && (
                        <div className={`${classes.taskDetails} task-details`} data-id="task-details">
                            {linkifyText(task.details)}
                        </div>
                    )}

                    <TaskListItemTags tags={task.tags} isAIGenerated={task.source?.type === 'ai-generated'} hideAIIcon={zone === 'note'} />

                    {task.completedAt && (
                        <div className={classes.completionStatus} data-id="task-completion-time">
                            {task.recurrenceEnded ? 'Completed Forever' : 'Completed'} {dayjs(task.completedAt).format('MMM D, YYYY [at] h:mm A')}
                        </div>
                    )}

                    {task.skippedAt && (
                        <div className={classes.completionStatus} data-id="task-skip-time">
                            Skipped {dayjs(task.skippedAt).format('MMM D, YYYY [at] h:mm A')}
                        </div>
                    )}

                </div>
            </IonLabel>
        </IonItem>
        </div>
    );
};
